import React, { useState } from 'react';
import './PromptAnalystPage.scss';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import type { CreatePromptAnalysisRequest } from '@brand24-original/llm-types';
import Title from '../../shared/components/title/Title';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS } from '../../shared/routes/path';
import { promptAnalystAccess } from '../../shared/helpers/addons/promptAnalystAccess';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { useGetPromptAnalyses } from '../../shared/api/hooks/prompt-analysis/useGetPromptAnalyses';
import { useGetPromptAnalysisUsage } from '../../shared/api/hooks/prompt-analysis/useGetPromptAnalysisUsage';
import { useCreatePromptAnalysis } from '../../shared/api/hooks/prompt-analysis/useCreatePromptAnalysis';
import PromptAnalystForm from './components/PromptAnalystForm';
import PromptAnalysisHistory from './components/PromptAnalysisHistory';
import PromptAnalysisView, { AddPromptAction } from './components/PromptAnalysisView';
import { usePutMonitorsForProject } from '../../shared/api/hooks/monitor/usePutMonitorsForProject';
import useCanAddProject from '../../shared/hooks/account/UseAddProject';
import useToast from '../../shared/hooks/global/UseToast';
import { MonitorQuestion } from '../../shared/models/monitor/MonitorQuestion';

const ERROR_KEYS: Record<string, string> = {
  monthly_limit_reached: 'promptAnalyst.errorMonthlyLimit',
  demo_project_read_only: 'promptAnalyst.errorDemo',
  'Invalid prompt': 'promptAnalyst.errorInvalidPrompt',
};

const HISTORY_COLLAPSED_KEY = 'chatbeat-prompt-analyst-history-collapsed';

const PromptAnalystPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const resetsAtLabel = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' });
  const isMobile = useIsMobile();
  const { accountResponse, projectResponse, refetchAccountSilently } = useAccount();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  // Jak w ChatGPT: wybrany przegląd siedzi w adresie (`?analysis=12`, działa „wstecz" i link), a bez niego
  // strona jest ekranem nowej analizy.
  const [searchParams, setSearchParams] = useSearchParams();
  const analysisId = Number(searchParams.get('analysis')) || null;
  const openAnalysis = (id: number | null) => setSearchParams(id ? { analysis: String(id) } : {});
  const projectId = Number(projectIdParam) > 0 ? Number(projectIdParam) : null;
  const canAccess = !!accountResponse && promptAnalystAccess(accountResponse).canAccess;

  // Jak lewe menu (`Layout`): stan zwinięcia zapamiętany między wejściami.
  const [isHistoryCollapsed, setHistoryCollapsed] = useState<boolean>(
    () => localStorage.getItem(HISTORY_COLLAPSED_KEY) === 'true'
  );
  const toggleHistory = () =>
    setHistoryCollapsed((collapsed) => {
      localStorage.setItem(HISTORY_COLLAPSED_KEY, String(!collapsed));
      return !collapsed;
    });
  const [formError, setFormError] = useState<string | null>(null);

  const { data: analyses, isLoading } = useGetPromptAnalyses(canAccess ? projectId : null);
  const { data: usage } = useGetPromptAnalysisUsage(canAccess && projectId != null);
  const { monitorPrompts } = useGetMonitorsForProject(canAccess ? projectId : null);
  const create = useCreatePromptAnalysis(projectId ?? 0, {
    onSuccess: (analysis) => openAnalysis(analysis.id),
  });
  const { addMonitorPrompts } = usePutMonitorsForProject(projectId ?? 0);
  const { canAddProject } = useCanAddProject();
  const { showToast, toastElement } = useToast();
  const [addingPrompt, setAddingPrompt] = useState<string | null>(null);

  if (!accountResponse) {
    return null;
  }
  if (!canAccess || projectId == null) {
    return <Navigate to={PROJECTS} replace />;
  }

  const handleSubmit = (body: CreatePromptAnalysisRequest) => {
    setFormError(null);
    create.mutate(body, {
      onError: (err) => {
        const code = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        setFormError(t(ERROR_KEYS[code] ?? 'promptAnalyst.errorGeneric'));
      },
    });
  };

  const isDemo = !!projectResponse?.find((p) => p.id === projectId)?.demoVersion;
  // Already monitored = same text as a project prompt (case/space-insensitive, like `countSimilarAccounts`).
  const monitoredPrompts = new Set((monitorPrompts ?? []).map((m) => m.prompt.trim().toLowerCase()));
  const addPrompt: AddPromptAction = {
    canAdd: (prompt) => !isDemo && !!canAddProject && !monitoredPrompts.has(prompt.trim().toLowerCase()),
    isAdding: (prompt) => addingPrompt === prompt,
    onAdd: async (prompt) => {
      setAddingPrompt(prompt);
      try {
        await addMonitorPrompts({
          projectId,
          monitorQuestions: [{ prompt, status: 'PENDING' } as MonitorQuestion],
        });
        showToast(t('suggestedPromptAdded'));
        // Silent: `refetchData` would unmount the layout; the prompt count (limit) must still refresh.
        await refetchAccountSilently();
      } catch {
        showToast(t('promptAnalyst.errorAddPrompt'), 'error');
      } finally {
        setAddingPrompt(null);
      }
    },
  };

  const selected = (analysisId && analyses?.find((a) => a.id === analysisId)) || null;
  // Przegląd z adresu, którego (już) nie ma na liście → ekran nowej analizy, nie wieczny szkielet.
  const showsReport = analysisId != null && (isLoading || selected != null);

  const quota =
    usage &&
    (usage.remaining <= 0
      ? t('promptAnalyst.quotaExhausted', { limit: usage.limit, date: resetsAtLabel(usage.resetsAt) })
      : t('promptAnalyst.quotaRemaining', {
          remaining: usage.remaining,
          limit: usage.limit,
          date: resetsAtLabel(usage.resetsAt),
        }));

  return (
    <div className="prompt-analyst-page">
      {toastElement}
      <div className="page-content prompt-analyst-page__content">
        <div className="page-box">
          <MobilePageHeader title={<span>{t('promptAnalyst.title')}</span>} />
          {/* Przyklejony nagłówek jak na Brands — globalny `page-box__filters` + `useStickyObserver` z `Panel`. */}
          {!isMobile && (
            <div className="page-box__filters" data-observe-sticky>
              <Title
                title={t('promptAnalyst.title')}
                childrenOnMain={<div className="title-box__subtitle">{t('promptAnalyst.subtitle')}</div>}
              >
                {quota && (
                  <span
                    className={`prompt-analyst-page__quota${
                      usage!.remaining <= 0 ? ' prompt-analyst-page__quota--exhausted' : ''
                    }`}
                  >
                    {quota}
                  </span>
                )}
              </Title>
            </div>
          )}

          <div className="page-box__content prompt-analyst-page__main">
            {showsReport ? (
              <PromptAnalysisView analysis={selected} addPrompt={addPrompt} />
            ) : (
              <div className="prompt-analyst-page__composer">
                <PromptAnalystForm
                  prompts={monitorPrompts ?? []}
                  isQuotaExhausted={!!usage && usage.remaining <= 0}
                  isBusy={create.isPending}
                  error={formError}
                  onSubmit={handleSubmit}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <PromptAnalysisHistory
        analyses={analyses}
        selectedId={showsReport ? analysisId : null}
        onSelect={openAnalysis}
        onNew={() => openAnalysis(null)}
        isCollapsed={isHistoryCollapsed}
        onToggleCollapse={toggleHistory}
      />
    </div>
  );
};

export default PromptAnalystPage;
